import { ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';

import useSystemBarsOverlay from '../hooks/useSystemBarsOverlay';
import { acquireScrollLock, releaseScrollLock } from '../utils/scrollLock';

import styles from './MobileDrawer.module.scss';

const SLIDE_ANIMATION_DURATION_MS = 350;

const BACKDROP_COLOR = 'rgba(18, 28, 51, 0.8)';
const SHEET_COLOR = '#191C2E';

type AnimationState = 'initial' | 'slideIn' | 'slideOut';

export type MobileDrawerProps = {
  isOpen: boolean;
  title: string;
  showTitle?: boolean;
  showCloseButton?: boolean;
  children: ReactNode;
  additionalClasses?: string;
  onClose: () => void;
  onCloseStart?: () => void;
  usePortal?: boolean;
};

const MobileDrawer = (props: MobileDrawerProps): JSX.Element | null => {
  const {
    isOpen,
    title,
    showTitle = true,
    showCloseButton = true,
    onClose,
    onCloseStart,
    children,
    usePortal,
    additionalClasses,
  } = props;
  const [animationState, setAnimationState] = useState<AnimationState>('initial');
  const [isVisible, setIsVisible] = useState(isOpen);
  const [closedInternally, setClosedInternally] = useState(false);
  const drawerBodyRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  const onCloseStartRef = useRef(onCloseStart);

  useEffect(() => {
    onCloseRef.current = onClose;
    onCloseStartRef.current = onCloseStart;
  }, [onClose, onCloseStart]);

  useEffect(() => {
    if (isOpen) setClosedInternally(false);
  }, [isOpen]);

  const effectiveIsOpen = isOpen && !closedInternally;

  useSystemBarsOverlay(effectiveIsOpen, { statusBar: BACKDROP_COLOR, navigationBar: SHEET_COLOR });

  useEffect(() => {
    if (!effectiveIsOpen || typeof document === 'undefined') return undefined;

    const preventScroll = (e: WheelEvent | TouchEvent) => {
      const target = e.target as Node;
      const insideDrawer = !!drawerBodyRef.current?.contains(target);

      if (insideDrawer) {
        return;
      }

      e.preventDefault();
    };

    acquireScrollLock();

    document.addEventListener('wheel', preventScroll, { passive: false });
    document.addEventListener('touchmove', preventScroll, { passive: false });

    return () => {
      releaseScrollLock();
      document.removeEventListener('wheel', preventScroll);
      document.removeEventListener('touchmove', preventScroll);
    };
  }, [effectiveIsOpen]);

  useEffect(() => {
    if (effectiveIsOpen) {
      setIsVisible(true);
      setAnimationState('initial');

      const frameId = requestAnimationFrame(() => {
        setAnimationState('slideIn');
      });

      return () => cancelAnimationFrame(frameId);
    }

    if (animationState !== 'slideIn') return undefined;

    setAnimationState('slideOut');
    // Notify the parent that closing has begun so it can sync UI
    onCloseStartRef.current?.();

    const timeoutId = setTimeout(() => {
      setIsVisible(false);
      onCloseRef.current?.();
    }, SLIDE_ANIMATION_DURATION_MS);

    return () => clearTimeout(timeoutId);
  }, [effectiveIsOpen]);

  const handleClose = () => {
    setClosedInternally(true);
  };

  if (animationState === 'initial' || !isVisible) {
    return null;
  }

  const drawer = (
    <div className={classNames(styles.containerMobile, additionalClasses)}>
      <button
        type="button"
        className={styles.background}
        aria-label="Close drawer"
        onClick={handleClose}
      />
      <div
        ref={drawerBodyRef}
        className={classNames(
          styles.body,
          {
            [styles.slideIn]: animationState === 'slideIn',
            [styles.slideOut]: animationState === 'slideOut',
          },
        )}
      >
        <div className={styles.header}>
          {showTitle && (<div className={styles.title}>{title}</div>)}
          {showCloseButton && (
            <div className={styles.closeButtonWrapper}>
              <button
                type="button"
                className={styles.closeButton}
                aria-label="Close"
                onClick={handleClose}
              />
            </div>
          )}
        </div>
        {children}
      </div>
    </div>
  );

  if (usePortal && typeof document !== 'undefined' && document.body) {
    return createPortal(drawer, document.body);
  }

  return drawer;
};

export default MobileDrawer;
